/**
 * Rolling-median anomaly-event detection.
 *
 * Each reading is compared to the median of a constant-width window centred on
 * it. The window is clamped — never truncated — at the series edges, so the
 * first and last readings are judged against a full-width, mostly one-sided
 * neighbourhood instead of a shrunken one that an edge excursion would
 * dominate. Residuals from that baseline are scored against a single robust
 * scale (MAD·1.4826 of all residuals — robust, so neither excursions nor
 * trends inflate it), and flagged readings cluster into events with
 * hysteresis: an event opens where a reading clears `threshold` and extends
 * over same-side neighbours above half of it. A sustained dip or hump is
 * therefore one event with a start, end, and peak — not a scatter of edge
 * points — and an event that runs through the last reading is flagged
 * `ongoing`: the signal was still away from normal when the window closed.
 *
 * To feed this from aggregate buckets (avg/min/max per granularity) rather
 * than raw points, pass the bucket max series to surface upward excursions or
 * the bucket min series for dropouts; the algorithm is identical.
 */
import { median, medianAbsoluteDeviation, standardDeviation } from 'simple-statistics';

import { EPSILON } from './constants.ts';
import type { AnomalyEvent, AnomalyResult, TimeseriesPoint } from './types.ts';

/** Scales MAD to estimate the standard deviation of normally-distributed data. */
const MAD_TO_SIGMA = 1.4826;

export interface AnomalyOptions {
  /**
   * Half-width of the rolling baseline window; the window spans `2·radius + 1`.
   * Defaults to a tenth of the series length clamped to [4, 24] — wide enough
   * that a multi-point excursion cannot become its own baseline.
   */
  windowRadius?: number;
  /**
   * Deviation threshold in MAD-scaled sigmas to open an event; an event
   * extends over adjacent same-side readings above half this. Default 3.
   */
  threshold?: number;
  /** Maximum events returned in `top`. Default 10. */
  topK?: number;
}

/** Robust scale (MAD·1.4826) of the values, falling back to σ when MAD is 0. */
function robustScale(values: number[]): number {
  if (values.length < 2) {
    return 0;
  }
  const mad = MAD_TO_SIGMA * medianAbsoluteDeviation(values);
  // MAD collapses to 0 when most values are identical; fall back to the
  // (non-robust) standard deviation so a lone spike in quiet data is still
  // measurable rather than silently undetectable.
  return mad > EPSILON ? mad : standardDeviation(values);
}

/**
 * Detect anomaly events. Returns the total event count and the strongest few
 * (by peak deviation) for the agent to call out.
 */
export function detectAnomalies(points: TimeseriesPoint[], options: AnomalyOptions = {}): AnomalyResult {
  const n = points.length;
  const radius = options.windowRadius ?? Math.min(24, Math.max(4, Math.floor(n / 10)));
  const threshold = options.threshold ?? 3;
  const topK = options.topK ?? 10;

  const values = points.map((p) => p.value);
  const width = Math.min(n, 2 * radius + 1);

  // Constant-width baseline: clamp the window's position, never its size.
  const baselines = values.map((_, i) => {
    const lo = Math.min(Math.max(0, i - radius), Math.max(0, n - width));
    return median(values.slice(lo, lo + width));
  });
  const residuals = values.map((v, i) => v - (baselines[i] ?? 0));
  const scale = robustScale(residuals);
  if (scale <= EPSILON) {
    return { count: 0, top: [] };
  }

  const scores = residuals.map((r) => Math.abs(r) / scale);
  const sides = residuals.map((r) => Math.sign(r));

  // Hysteresis clustering: a reading joins the open run while it stays
  // contiguous, on the same side of the baseline, and above half the
  // threshold; runs survive only when at least one reading clears the full
  // threshold.
  const runs = scores.reduce<number[][]>((acc, score, i) => {
    if (score < threshold / 2) {
      return acc;
    }
    const run = acc.at(-1);
    if (run && run.at(-1) === i - 1 && sides[run[0] ?? 0] === sides[i]) {
      run.push(i);
      return acc;
    }
    acc.push([i]);
    return acc;
  }, []);

  const events = runs
    .filter((run) => run.some((i) => (scores[i] ?? 0) > threshold))
    .flatMap<AnomalyEvent>((run) => {
      const peak = run.reduce((a, b) => ((scores[a] ?? 0) >= (scores[b] ?? 0) ? a : b));
      const first = points[run[0] ?? 0];
      const last = points[run.at(-1) ?? 0];
      const peakPoint = points[peak];
      if (!first || !last || !peakPoint) {
        return [];
      }
      const baseline = baselines[peak] ?? 0;
      const type = (sides[peak] ?? 0) > 0 ? 'spike' : 'dip';
      // A percentage needs a baseline distinguishable from zero — when the
      // level itself sits within noise of zero, only the sigma score remains.
      const deviationPct =
        Math.abs(baseline) > scale ? ((peakPoint.value - baseline) / Math.abs(baseline)) * 100 : null;
      return [
        {
          label: type === 'spike' ? 'unusual spike' : 'unusual dip',
          start: first.timestamp,
          end: last.timestamp,
          pointCount: run.length,
          extremeValue: peakPoint.value,
          extremeTimestamp: peakPoint.timestamp,
          deviationSigma: scores[peak] ?? 0,
          deviationPct,
          ongoing: run.at(-1) === n - 1,
          type,
        },
      ];
    });

  const top = [...events].sort((a, b) => b.deviationSigma - a.deviationSigma).slice(0, topK);
  return { count: events.length, top };
}
